import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft } from 'lucide-react';
import { Button, Card, EmptyState, FilterBar, GroupByDropdown, ViewSwitcher, CalendarNavigation, ListDateCarousel, PrintButton } from '@/components/ui';
import { WeeklyView, MonthlyView } from '@/components/calendar';
import { useItemStore, useUIStore, useSettingsStore } from '@/stores';
import { DateList } from './DateList';
import { isRecurringItem, extendItemOccurrences } from '@perfin/shared';
import type { TabKey } from '@/stores/uiStore';

export function DatesPage() {
  const navigate = useNavigate();
  const dates = useItemStore((state) => state.getKeyDates());
  const { activeTab, setActiveTab, categoryFilters, pageFilters, viewMode, calendarOffset, groupBy } = useUIStore();
  const { currentUserId, getCategoriesForType, members } = useSettingsStore();
  const isAdmin = useSettingsStore((state) => state.currentUserRole === 'admin' || state.currentUserRole === 'superAdmin');

  const currentViewMode = viewMode?.dates ?? 'card';
  const currentTab = activeTab?.dates ?? 'today';
  const currentGroupBy = groupBy?.dates ?? 'date';
  const pFilters = pageFilters?.dates ?? { ownerFilter: null, statusFilter: null, priorityFilter: null };

  // Calendar views show all scheduled AND recurring items
  const isCalendarView = currentViewMode === 'weekly' || currentViewMode === 'monthly';
  const effectiveTab = isCalendarView ? 'allScheduledAndRecurring' : currentTab;

  // Bug #2: Auto-switch groupBy when 'date' is invalid for All Recurring tab
  const { setGroupBy } = useUIStore();
  React.useEffect(() => {
    if (currentTab === 'allRecurring' && currentGroupBy === 'date') {
      setGroupBy('dates', 'category');
    }
  }, [currentTab, currentGroupBy, setGroupBy]);

  // Bug #5: Validate owner filter - if references non-existent user, reset to current user
  const { setOwnerFilter } = useUIStore();
  React.useEffect(() => {
    if (pFilters.ownerFilter && members.length > 0) {
      const ownerExists = members.some(m => m.userId === pFilters.ownerFilter);
      if (!ownerExists && currentUserId) {
        setOwnerFilter('dates', currentUserId);
      }
    }
  }, [pFilters.ownerFilter, members, currentUserId, setOwnerFilter]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'allScheduled', label: 'All Scheduled' },
    { key: 'allUnscheduled', label: 'All Unscheduled' },
    { key: 'allRecurring', label: 'All Recurring' },
    { key: 'pastWeek', label: 'Past Week' },
    { key: 'today', label: 'Today' },
    { key: 'nextWeek', label: 'Next Week' },
  ];

  // Get reference date from store
  const getReferenceDate = useUIStore((state) => state.getReferenceDate);
  const referenceDate = getReferenceDate('dates');

  const filteredDates = useMemo(() => {
    const refDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
    const refDatePlusOne = new Date(refDate.getTime() + 24 * 60 * 60 * 1000);
    const sevenDaysBefore = new Date(refDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAfter = new Date(refDate.getTime() + 7 * 24 * 60 * 60 * 1000);

    const parseDate = (d: unknown): Date | null => {
      if (!d) return null;
      if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
        return (d as { toDate: () => Date }).toDate();
      }
      if (d instanceof Date) return d;
      if (typeof d === 'object' && d !== null && 'seconds' in d) {
        return new Date((d as { seconds: number }).seconds * 1000);
      }
      return new Date(d as string | number);
    };

    let result = dates;

    // 1. Apply filters FIRST (category, status, priority)
    const filter = categoryFilters.dates;
    if (filter.categoryId) {
      result = result.filter((keyDate) => keyDate.category === filter.categoryId);
    }
    if (filter.subCategoryId) {
      result = result.filter((keyDate) => keyDate.subCategory === filter.subCategoryId);
    }
    if (pFilters?.itemGroupFilter) {
      result = result.filter((keyDate) => keyDate.itemGroup === pFilters.itemGroupFilter);
    }

    // Status filter: for time-based tabs, only apply to non-recurring items
    // (recurring items will be filtered by occurrence status in DateList)
    const isTimeBased = effectiveTab === 'pastWeek' || effectiveTab === 'today' || effectiveTab === 'nextWeek';
    if (pFilters.statusFilter) {
      result = result.filter((keyDate) => {
        if (isTimeBased && isRecurringItem(keyDate)) {
          return true; // Let DateList filter by occurrence status
        }
        const dateStatus = (keyDate as unknown as { status?: string }).status;
        return dateStatus === pFilters.statusFilter;
      });
    }

    if (pFilters.priorityFilter) {
      result = result.filter((keyDate) => keyDate.priority === pFilters.priorityFilter);
    }

    // Owner filtering
    if (pFilters?.ownerFilter) {
      result = result.filter((keyDate) => keyDate.owner === pFilters.ownerFilter);
    } else if (!isAdmin && currentUserId) {
      // Standard users without owner filter: show items they own OR created
      result = result.filter((keyDate) => keyDate.owner === currentUserId || keyDate.createdBy === currentUserId);
    }

    // 2. Apply tab (time) filter on the result (use effectiveTab for calendar view compatibility)
    switch (effectiveTab) {
      case 'allScheduledAndRecurring':
        // For calendar views: include scheduled non-recurring items AND all recurring items
        result = result.filter((keyDate) => {
          if (isRecurringItem(keyDate)) return true; // Include recurring
          const dueDate = parseDate(keyDate.dueDate);
          return !!dueDate; // Include scheduled non-recurring
        });
        break;

      case 'allScheduled':
        // NON-RECURRING items with dueDate
        result = result.filter((keyDate) => {
          if (isRecurringItem(keyDate)) return false;
          const dueDate = parseDate(keyDate.dueDate);
          return !!dueDate;
        });
        break;

      case 'allUnscheduled':
        // NON-RECURRING items with no due date
        result = result.filter((keyDate) => {
          if (isRecurringItem(keyDate)) return false;
          const dueDate = parseDate(keyDate.dueDate);
          return !dueDate;
        });
        break;

      case 'allRecurring':
        // All recurring items - NO tile replication
        result = result.filter((keyDate) => isRecurringItem(keyDate));
        break;

      case 'pastWeek':
        // Scheduled + Recurring (excludes unscheduled)
        result = result.filter((keyDate) => {
          if (isRecurringItem(keyDate)) {
            const occurrences = (keyDate as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= sevenDaysBefore && occDate < refDate;
            });
          }
          const dueDate = parseDate(keyDate.dueDate);
          return dueDate && dueDate >= sevenDaysBefore && dueDate < refDate;
        });
        break;

      case 'today':
        // Scheduled + Recurring on refDate (excludes unscheduled)
        result = result.filter((keyDate) => {
          if (isRecurringItem(keyDate)) {
            const occurrences = (keyDate as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDate && occDate < refDatePlusOne;
            });
          }
          const dueDate = parseDate(keyDate.dueDate);
          return dueDate && dueDate >= refDate && dueDate < refDatePlusOne;
        });
        break;

      case 'nextWeek':
        // Scheduled + Recurring (excludes unscheduled)
        result = result.filter((keyDate) => {
          if (isRecurringItem(keyDate)) {
            const occurrences = (keyDate as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDatePlusOne && occDate <= sevenDaysAfter;
            });
          }
          const dueDate = parseDate(keyDate.dueDate);
          return dueDate && dueDate >= refDatePlusOne && dueDate <= sevenDaysAfter;
        });
        break;
    }

    return result;
  }, [dates, effectiveTab, categoryFilters.dates, pFilters, currentUserId, isAdmin, referenceDate]);

  const handleAddDate = () => navigate('/items/new?mode=create&type=keyDate');

  // Handler for extending occurrences when navigating beyond 180-day horizon
  const handleNavigateBeyondHorizon = useCallback(async (targetDate: Date) => {
    const recurringIds = dates
      .filter((date) => isRecurringItem(date))
      .map((date) => date.id);

    if (recurringIds.length > 0) {
      try {
        await extendItemOccurrences(recurringIds, targetDate);
      } catch (error) {
        console.error('Failed to extend occurrences:', error);
      }
    }
  }, [dates]);

  // Helper functions for PDF generation
  const dateCategories = getCategoriesForType('keyDate');
  const getCategoryName = (categoryId: string) => {
    const cat = dateCategories.find((c) => c.id === categoryId);
    return cat?.name || categoryId;
  };
  const getOwnerName = (ownerId: string) => {
    const member = members.find((m) => m.userId === ownerId);
    return member?.displayName || ownerId;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Key Dates</h1>
          <PrintButton
            title="Key Dates"
            items={filteredDates}
            getCategoryName={getCategoryName}
            getOwnerName={getOwnerName}
          />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <FilterBar itemType="keyDate" pageKey="dates" hideStatusFilter={currentTab === 'allRecurring'} />
        <hr className="w-full border-gray-300 md:hidden" />
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          {currentViewMode === 'card' && <GroupByDropdown pageKey="dates" showInfoTooltip excludeOptions={currentTab === 'allRecurring' ? ['date'] : []} />}
          <Button onClick={handleAddDate}>
            Add Date
          </Button>
          <ViewSwitcher pageKey="dates" />
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex gap-1 flex-wrap md:flex-nowrap">
          {/* In calendar view, show single "All Scheduled and Recurring" tab */}
          {isCalendarView ? (
            <button className="px-4 py-2 text-sm font-medium border-b-2 border-primary-600 text-primary-600">
              All Scheduled and Recurring
            </button>
          ) : (
            tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab('dates', tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  currentTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))
          )}
        </div>
        {currentViewMode === 'card' && (currentTab === 'pastWeek' || currentTab === 'today' || currentTab === 'nextWeek') && (
          <ListDateCarousel pageKey="dates" />
        )}
        {currentViewMode !== 'card' && (
          <CalendarNavigation
            pageKey="dates"
            mode={currentViewMode}
            onNavigateBeyondHorizon={handleNavigateBeyondHorizon}
          />
        )}
      </div>

      {currentViewMode === 'card' && (
        filteredDates.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Calendar className="h-12 w-12" />}
              title="No dates found"
              description={
                currentTab === 'allScheduled'
                  ? 'Add your first important date'
                  : 'No dates in this view'
              }
              action={
                currentTab === 'allScheduled'
                  ? { label: 'Add Date', onClick: handleAddDate }
                  : undefined
              }
            />
          </Card>
        ) : (
          <DateList dates={filteredDates} groupBy={currentGroupBy} currentTab={currentTab} hideStatusToggle={currentTab === 'allRecurring'} referenceDate={referenceDate} statusFilter={pFilters.statusFilter} />
        )
      )}

      {currentViewMode === 'weekly' && (
        <WeeklyView
          items={filteredDates}
          offset={calendarOffset.dates.weekly}
          basePath="/dates"
        />
      )}

      {currentViewMode === 'monthly' && (
        <MonthlyView
          items={filteredDates}
          offset={calendarOffset.dates.monthly}
          basePath="/dates"
        />
      )}
    </div>
  );
}
